import { apiRequest, apiRequestBlob } from '../lib/httpClient'
import { fetchDetailInit } from './detailInit'
import type { InitDetailRequest } from '../lib/initDetail'
import type {
  DashboardLinkedEntry,
  ReportListResult,
  ReportEditInitResponse,
  PowerBIReportOption,
  PowerBIReportApiRow,
  PowerBIReportPage,
  PowerBIReportPageApiRow,
  PowerBIEmbedConfig,
  PowerBIEmbedApiResponse,
  ExportStatusResponse,
  ExportStatusApiResponse,
  ExportInitApiResponse,
  ReportGridFilters,
  ReportFormValues,
  ReportTypeId,
  UserOption,
} from '../types/dashboard'

function mapPowerBIReport(row: PowerBIReportApiRow): PowerBIReportOption {
  const reportId = row.reportId || row.ReportId || ''
  return {
    id: row.id || row.Id || row.value || row.Value || reportId,
    name: row.name || row.Name || '',
    reportId: reportId || row.id || row.Id || row.value || row.Value || '',
  }
}

function mapReportPage(row: PowerBIReportPageApiRow): PowerBIReportPage {
  return {
    reportId: row.reportId || row.ReportId || '',
    value: row.value || row.Value || '',
    name: row.name || row.Name || '',
  }
}

export async function fetchReportList(
  request: InitDetailRequest,
  filters: ReportGridFilters = { name: '', powerBIReportName: null }
): Promise<ReportListResult> {
  const payload = await fetchDetailInit(request)
  let reports = payload.reports
  const groupOptions = payload.groupOptions

  if (filters.name?.trim()) {
    const q = filters.name.trim().toLowerCase()
    reports = reports.filter((r) => r.name.toLowerCase().includes(q))
  }
  if (filters.powerBIReportName?.trim()) {
    const q = filters.powerBIReportName.trim().toLowerCase()
    reports = reports.filter((r) => r.powerBIReportName.toLowerCase().includes(q))
  }

  return { reports, groupOptions }
}

// --- CRUD ---

export async function fetchReportEditInit(reportId: string): Promise<ReportEditInitResponse> {
  // Use Record so we can handle both camelCase and PascalCase keys from the API
  const payload = await apiRequest<Record<string, unknown>>('report/ui/edit/initialise', {
    method: 'GET',
    query: { reportId },
  })

  const raw = (payload['report'] || payload['Report'] || {}) as Record<string, unknown>
  const options = ((payload['options'] || payload['Options']) as Record<string, unknown> | undefined) || {}

  const toNum = (v: unknown): number | null => (v != null ? Number(v) : null)

  const report: ReportFormValues = {
    reportId: (raw['reportId'] || raw['ReportId'] || reportId) as string,
    clientId: (raw['clientId'] || raw['ClientId'] || '') as string,
    name: (raw['name'] || raw['Name'] || '') as string,
    statusId: toNum(raw['statusId'] ?? raw['StatusId']) ?? 1,
    powerBIReport: (raw['powerBIReport'] || raw['PowerBIReport'] || '') as string,
    powerBIReportName: (raw['powerBIReportName'] || raw['PowerBIReportName'] || '') as string,
    groupIds: (raw['groupIds'] || raw['GroupIds'] || []) as string[],
    userIds: (raw['userIds'] || raw['UserIds'] || []) as string[],
    roleName: (raw['roleName'] || raw['RoleName'] || null) as string | null,
    rowLevelSecurity: Boolean(
      raw['rowLevelSecurity'] ?? raw['RowLevelSecurity'] ?? !!(raw['roleName'] || raw['RoleName'])
    ),
    powerBIReportPage: (raw['powerBIReportPage'] || raw['PowerBIReportPage'] || '') as string,
    secondRoleName: (raw['secondRoleName'] || raw['SecondRoleName'] || null) as string | null,
    powerBIWorkspace: (raw['powerBIWorkspace'] || raw['PowerBIWorkspace'] || null) as string | null,
    platformType: toNum(raw['platformType'] ?? raw['PlatformType']),
    typeId: toNum(raw['typeId'] ?? raw['TypeId']) as ReportTypeId | null,
  }

  const rawStatusOpts = (payload['statusOptions'] || payload['StatusOptions'] || []) as Array<Record<string, unknown>>
  const rawGroupOpts = (options['groups'] || options['userGroups'] || options['Groups'] || options['UserGroups'] || []) as Array<Record<string, unknown>>
  const rawUserOpts = (options['users'] || options['Users'] || []) as Array<Record<string, unknown>>
  const rawPbiOpts = (options['reports'] || options['Reports'] || payload['powerbiReportOptions'] || payload['PowerbiReportOptions'] || payload['PowerBIReportOptions'] || []) as Array<Record<string, unknown>>
  const rawPageOpts = (options['powerBiReportPages'] || options['PowerBiReportPages'] || options['powerBIReportPages'] || options['PowerBIReportPages'] || []) as Array<Record<string, unknown>>
  const rawDashboardLinkedOpts = (options['dashboardLinkedOptions'] || options['DashboardLinkedOptions'] || []) as Array<Record<string, unknown>>

  return {
    report,
    statusOptions: rawStatusOpts.map((s) => ({
      value: toNum(s['value'] ?? s['Value']) ?? 0,
      name: (s['name'] || s['Name'] || '') as string,
    })),
    userGroupOptions: rawGroupOpts.map((g) => ({
      value: (g['value'] || g['Value'] || g['groupId'] || g['GroupId'] || '') as string,
      name: (g['name'] || g['Name'] || '') as string,
    })),
    userOptions: rawUserOpts.map(
      (u): UserOption => ({
        value: (u['value'] || u['Value'] || u['userId'] || u['UserId'] || '') as string,
        name: (u['name'] || u['Name'] || u['label'] || u['Label'] || u['fullName'] || u['FullName'] || '') as string,
      })
    ),
    powerbiReportOptions: rawPbiOpts.map((p) => {
      const pReportId = (p['reportId'] || p['ReportId'] || '') as string
      const pId = (p['id'] || p['Id'] || p['value'] || p['Value'] || pReportId) as string
      return {
        id: pId,
        name: (p['name'] || p['Name'] || '') as string,
        reportId: pReportId || pId,
      }
    }),
    powerbiReportPages: rawPageOpts.map((p) => ({
      reportId: (p['reportId'] || p['ReportId'] || report.powerBIReport || '') as string,
      value: (p['value'] || p['Value'] || '') as string,
      name: (p['name'] || p['Name'] || '') as string,
    })),
    dashboardLinkedOptions: rawDashboardLinkedOpts.map(
      (d): DashboardLinkedEntry => ({
        name: (d['pageName'] || d['PageName'] || d['name'] || d['Name'] || '') as string,
        roleName: (d['roleName'] || d['RoleName'] || '') as string,
      })
    ),
    workspaceName: (payload['workspaceName'] || payload['WorkspaceName'] || '') as string,
    workspace: (payload['workspace'] || payload['Workspace'] || '') as string,
  }
}

export async function createReport(payload: unknown): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('report/ui/create/save', {
    method: 'POST',
    body: payload,
  })
}

export async function updateReport(payload: unknown): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('report/ui/edit/save', {
    method: 'POST',
    body: payload,
  })
}

export async function deleteReport(reportId: string): Promise<void> {
  await apiRequest<void>(`report/ui/edit/delete/${reportId}`, {
    method: 'POST',
    body: {},
  })
}

// --- Power BI reports & pages ---

export async function fetchPowerBIReports(clientId: string): Promise<PowerBIReportOption[]> {
  const payload = await apiRequest<PowerBIReportApiRow[]>('report/ui/pbi/reports', {
    method: 'GET',
    query: { clientId },
  })

  if (!Array.isArray(payload)) return []
  return payload.map(mapPowerBIReport)
}

export async function fetchReportPages(reportId: string): Promise<PowerBIReportPage[]> {
  const payload = await apiRequest<PowerBIReportPageApiRow[]>('report/ui/pbi/reports/GetPages', {
    method: 'GET',
    query: { reportId },
  })

  if (!Array.isArray(payload)) return []
  return payload.map(mapReportPage)
}

// --- Power BI embed ---

export async function fetchPowerBIEmbed(reportId: string): Promise<PowerBIEmbedConfig> {
  const raw = await apiRequest<PowerBIEmbedApiResponse>('powerbi/report/embed', {
    method: 'GET',
    query: { reportId },
  })

  return {
    token: raw.token || raw.Token || '',
    embedURL: raw.embedURL || raw.EmbedURL || raw.embedUrl || raw.EmbedUrl || '',
    pageName: raw.pageName || raw.PageName || '',
    expiration: raw.expiration || raw.Expiration || '',
    reportId: raw.reportId || raw.ReportId || '',
  }
}

// --- Export ---

export async function initiateReportExport(request: unknown): Promise<{ exportId: string }> {
  const raw = await apiRequest<ExportInitApiResponse>('report/ui/pbi/reports/DownloadReport', {
    method: 'POST',
    body: request,
  })

  return {
    exportId: raw.exportId || raw.ExportId || '',
  }
}

export async function fetchExportStatus(reportId: string, exportId: string): Promise<ExportStatusResponse> {
  const raw = await apiRequest<ExportStatusApiResponse>('report/ui/pbi/reports/CheckFileExportStatus', {
    method: 'GET',
    query: { reportId, exportId },
  })

  return {
    status: raw.status || raw.Status || 'NotStarted',
    resourceLocation: raw.resourceLocation || raw.ResourceLocation,
  }
}

export async function fetchExportedFile(resourceUrl: string): Promise<Blob> {
  const { blob } = await apiRequestBlob('report/ui/pbi/reports/ExportFile', {
    method: 'GET',
    query: { resourceLocation: resourceUrl },
  })
  return blob
}

// --- Usage tracking ---

export async function trackPowerBIUsage(events: unknown[]): Promise<void> {
  await apiRequest<void>('powerbi/usage/track', {
    method: 'POST',
    body: events,
  })
}
